import React, { useCallback, useEffect, useState } from 'react';
import { Bot, Save, ShieldAlert } from 'lucide-react';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const defaultSettings = {
  enabled: true,
  ai_enabled: true,
  ai_respond_on_quick_reply_cooldown: true,
  employee_reply_pause_seconds: 300,
  bot_name: '',
  system_prompt: '',
  reply_in_groups: false,
  ignored_numbers: [], 
};

// ==========================================
// КОМПОНЕНТ КРАСИВОГО БЕЙДЖА BETA С АНИМАЦИЕЙ
// ==========================================
function BetaBadge() {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div 
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span 
        style={{
          backgroundColor: '#fef08a', 
          color: '#854d0e',
          fontSize: '11px',
          fontWeight: '700',
          padding: '2px 6px',
          borderRadius: '4px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          cursor: 'help',
          border: '1px solid #fde047'
        }}
      >
        Beta
      </span>
      
      {/* Анимированная всплывающая подсказка */}
      <div style={{
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        // Анимация: плавно сдвигается вверх и появляется
        transform: `translate(-50%, ${showTooltip ? '-8px' : '0px'})`,
        opacity: showTooltip ? 1 : 0,
        visibility: showTooltip ? 'visible' : 'hidden',
        transition: 'all 0.2s ease-out',
        backgroundColor: '#1f2937', // Темно-серый фон
        color: '#f9fafb',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        lineHeight: '1.4',
        width: '240px',
        textAlign: 'center',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        zIndex: 100,
        pointerEvents: 'none', // чтобы подсказка не мешала мышке
        textTransform: 'none',
        fontWeight: 'normal',
        letterSpacing: 'normal'
      }}>
        Функция использует нейросети на этапе раннего доступа. ИИ может иногда ошибаться или давать неточные ответы.
        
        {/* Маленький треугольник (хвостик) внизу подсказки */}
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: '5px',
          borderStyle: 'solid',
          borderColor: '#1f2937 transparent transparent transparent'
        }} />
      </div>
    </div>
  );
}

// ==========================================
// КОМПОНЕНТ ПЕРЕКЛЮЧАТЕЛЯ
// ==========================================
function Toggle({ checked, onChange, label, description, badge }) {
  return (
    <div className="whatsapp-setting-row">
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <strong>{label}</strong>
          {badge && badge}
        </div>
        <span>{description}</span>
      </div>
      <button 
        className={`whatsapp-toggle ${checked ? 'whatsapp-toggle--on' : ''}`} 
        type="button" 
        role="switch" 
        aria-checked={checked} 
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </div>
  );
}

// ==========================================
// КОМПОНЕНТ ВВОДА НОМЕРОВ
// ==========================================
function PhoneInputTags({ tags, onChange }) {
  const [inputValue, setInputValue] = useState('');

  const normalizePhoneNumber = (raw) => {
    let cleaned = raw.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+7')) cleaned = '7' + cleaned.slice(2);
    if (cleaned.startsWith('8') && cleaned.length === 11) cleaned = '7' + cleaned.slice(1);
    cleaned = cleaned.replace(/\D/g, '');
    return cleaned;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      const normalizedTag = normalizePhoneNumber(inputValue);
      if (normalizedTag && !tags.includes(normalizedTag)) {
        onChange([...tags, normalizedTag]);
      }
      setInputValue('');
    } 
    else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  const removeTag = (indexToRemove) => {
    onChange(tags.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px',
      border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#fff',
      alignItems: 'center', minHeight: '42px'
    }}>
      {tags.map((tag, index) => (
        <span key={index} style={{
          display: 'flex', alignItems: 'center', backgroundColor: '#e0e7ff',
          color: '#3730a3', padding: '4px 10px', borderRadius: '16px',
          fontSize: '13px', fontWeight: '500'
        }}>
          {tag}
          <button 
            type="button" 
            onClick={() => removeTag(index)}
            style={{
              background: 'none', border: 'none', color: '#4338ca',
              marginLeft: '6px', cursor: 'pointer', fontSize: '16px',
              lineHeight: '1', padding: 0, display: 'flex'
            }}
          >
            &times;
          </button>
        </span>
      ))}
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={tags.length === 0 ? "Введите номер и нажмите Enter..." : "Добавить номер..."}
        style={{
          border: 'none', outline: 'none', flex: 1, minWidth: '160px',
          padding: '2px 4px', fontSize: '14px', backgroundColor: 'transparent'
        }}
      />
    </div>
  );
}

// ==========================================
// ОСНОВНОЙ КОМПОНЕНТ
// ==========================================
export default function WhatsAppAIOptions({ selectedBranchId }) {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');

  const load = useCallback(async () => {
    if (!selectedBranchId) return;
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/whatsapp/bot-settings?branchId=${selectedBranchId}`, { 
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } 
      });
      const result = await response.json();
      
      if (response.status === 404) {
        setSettings(defaultSettings);
        setFeedback('Подключите WhatsApp для этого филиала, чтобы настроить AI.');
        return;
      }
      if (!response.ok) throw new Error(result.error || 'Не удалось загрузить настройки AI');
      
      const loadedSettings = { ...defaultSettings, ...result.settings };
      loadedSettings.ignored_numbers = Array.isArray(loadedSettings.ignored_numbers) ? loadedSettings.ignored_numbers : [];
      setSettings(loadedSettings);
      setFeedback('');
    } catch (error) { 
      setFeedback(error.message); 
    } finally { 
      setLoading(false); 
    }
  }, [selectedBranchId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/whatsapp/bot-settings`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: `Bearer ${localStorage.getItem('token') || ''}` 
        },
        body: JSON.stringify({
          branchId: selectedBranchId,
          enabled: settings.enabled,
          aiEnabled: settings.ai_enabled,
          aiRespondOnQuickReplyCooldown: settings.ai_respond_on_quick_reply_cooldown,
          employeeReplyPauseSeconds: settings.employee_reply_pause_seconds,
          botName: settings.bot_name,
          systemPrompt: settings.system_prompt,
          replyInGroups: settings.reply_in_groups,
          ignoredNumbers: settings.ignored_numbers,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Не удалось сохранить настройки AI');
      setFeedback('Настройки AI сохранены');
    } catch (error) { 
      setFeedback(error.message); 
    } finally { 
      setLoading(false); 
    }
  };

  const update = (field, value) => setSettings((current) => ({ ...current, [field]: value }));

  return (
    <article className="whatsapp-panel whatsapp-panel--accent">
      <div className="whatsapp-panel__topline">
        <Bot size={18} />
        <span>Опция ответа/AI-ответы</span>
      </div>
      <h2>Функция ответа</h2>
      <p className="whatsapp-muted">Готовые ответы имеют приоритет. Если они не подходят, AI использует сведения о компании и историю диалога.</p>
      
      <Toggle 
        checked={settings.enabled} 
        onChange={(value) => update('enabled', value)} 
        label="Автоматические ответы" 
        description="Разрешить боту отвечать на входящие сообщения." 
      />

      <div className={settings.enabled ? '' : 'whatsapp-subsettings--disabled'}>
        
        <div style={{
          margin: '24px 0', padding: '20px', 
          backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <ShieldAlert size={18} color="#4b5563" />
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#374151' }}>
              Исключения для чата
            </h3>
          </div>
          
          <Toggle 
            checked={settings.reply_in_groups} 
            onChange={(value) => update('reply_in_groups', value)} 
            label="Отвечать в группах" 
            description="Разрешить боту реагировать на сообщения в беседах." 
          />
          
          <label className="whatsapp-text-field" style={{ marginTop: '16px', display: 'block' }}>
            <span style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Игнорируемые номера</span>
            <PhoneInputTags 
              tags={settings.ignored_numbers} 
              onChange={(newTags) => update('ignored_numbers', newTags)} 
            />
            <span className="whatsapp-muted" style={{ fontSize: '12px', marginTop: '6px', display: 'block' }}>
              Введите номер телефона и нажмите Enter. Бот не будет отвечать этим контактам.
            </span>
          </label>
        </div>

        {/* ПЕРЕКЛЮЧАТЕЛЬ С АНИМИРОВАННЫМ БЕЙДЖЕМ */}
        <Toggle 
          checked={settings.ai_enabled} 
          onChange={(value) => update('ai_enabled', value)} 
          label="Свободные ответы AI" 
          badge={<BetaBadge />}
          description="Ответить самостоятельно, когда готовый ответ не найден." 
        />
        
        <Toggle 
          checked={settings.ai_respond_on_quick_reply_cooldown} 
          onChange={(value) => update('ai_respond_on_quick_reply_cooldown', value)} 
          label="AI при cooldown готового ответа" 
          description="Ответить через AI, если нужный готовый ответ временно недоступен." 
        />
        
        <label className="whatsapp-text-field">
          Пауза после ответа сотрудника, секунд
          <input 
            type="number" 
            min="0" 
            value={settings.employee_reply_pause_seconds} 
            onChange={(event) => update('employee_reply_pause_seconds', event.target.value)} 
          />
        </label>
        <label className="whatsapp-text-field">
          Имя бота
          <input 
            value={settings.bot_name || ''} 
            onChange={(event) => update('bot_name', event.target.value)} 
            placeholder="Например: Помощник Another World" 
          />
        </label>
        <label className="whatsapp-text-field">
          Информация о компании
          <textarea 
            rows="12" 
            value={settings.system_prompt || ''} 
            onChange={(event) => update('system_prompt', event.target.value)} 
            placeholder="Название, описание, услуги и цены, пакеты, адреса, график, правила, акции и другая информация, которую AI может использовать в ответах." 
          />
        </label>
      </div>
      
      <button className="whatsapp-button" type="button" disabled={loading || !selectedBranchId} onClick={save}>
        <Save size={16} /> {loading ? 'Сохранение...' : 'Сохранить настройки AI'}
      </button>
      
      {feedback && (
        <p className={feedback === 'Настройки AI сохранены' ? 'whatsapp-feedback whatsapp-feedback--success' : 'whatsapp-feedback'}>
          {feedback}
        </p>
      )}
    </article>
  );
}